/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, TextField, ActionButton, ErrorText, Text } from '@components/atoms'
import { I18nEditor, Modal } from '@components/molecules'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

import {
	useAppDispatch,
	useAppSelector,
	accessControlActions,
	CrudType,
} from '@redux/index'
import { AccessGroupType, I18nType } from '@schemas'

interface PropsType {
	show?: boolean
	onDelete: () => void
}

/** 그룹 이름 수정 + 다국어. 삭제는 텍스트, 저장은 고스트 — 이 패널의 블랙은 없다 */
export const AccessGroupEditor = (props: PropsType) => {
	const { show, onDelete } = props

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const crud: CrudType = useAppSelector(state => state.auth.currentAccess)
	const groupList: AccessGroupType[] = useAppSelector(
		state => state.accessControl.groupList,
	)
	const selectedAccessGroup: AccessGroupType = useAppSelector(
		state => state.accessControl.selectedAccessGroup,
	)

	// state
	const [showModal, setShowModal] = React.useState<boolean>(false)
	const [groupName, setGroupName] = React.useState<string>('')
	const [errorMessage, setErrorMessage] = React.useState<string>('')

	// ref
	const textfieldRef = React.useRef<React.ElementRef<typeof TextField> | null>(null)

	// auto focus
	React.useEffect(() => {
		if (!show && textfieldRef !== null) {
			setTimeout(() => {
				textfieldRef.current?.blur()
			}, 100)
		}
	}, [show])

	React.useEffect(() => {
		setGroupName(selectedAccessGroup.name)
	}, [selectedAccessGroup])

	const onI18nEditorSave = (newI18n: I18nType) => {
		reduxDispatch(accessControlActions.UPDATE_ACCESS_GROUP_I18N(newI18n))
	}

	const onSave = React.useCallback(() => {
		if (selectedAccessGroup.name === '') {
			setErrorMessage('이름을 입력해주세요.')
		} else {
			const groupNameArr: string[] = groupList.reduce(
				(newGroupNameArr: string[], aPrevGroup: AccessGroupType) => {
					if (aPrevGroup.id !== selectedAccessGroup.id) {
						newGroupNameArr.push(aPrevGroup.name.toLowerCase())
					}

					return newGroupNameArr
				},
				[],
			)

			if (groupNameArr.includes(selectedAccessGroup.name.toLowerCase())) {
				setErrorMessage('이미 존재하는 이름입니다.')
			} else {
				reduxDispatch(accessControlActions.UPDATE_GROUP(selectedAccessGroup))
			}
		}
	}, [selectedAccessGroup])

	const onDeleteAlarm = () => {
		setShowModal(true)
	}

	const onGroupNameChange = React.useCallback(
		(e: any) => {
			const newName: string = e.target.value
			const newAddGroup = { ...selectedAccessGroup, name: newName }
			setGroupName(newName)
			reduxDispatch(accessControlActions.SELECTED_ACCESS_GROUP(newAddGroup))
		},
		[selectedAccessGroup],
	)

	return (
		<View css={style}>
			{selectedAccessGroup.id !== '' && (
				<>
					<View className="editGroupOuter">
						<View className="editGroupView">
							<span className="editGroupLabel">그룹 이름 수정</span>

							<TextField
								size="small"
								label="이름"
								variant="standard"
								inputRef={textfieldRef}
								autoFocus={true}
								value={groupName}
								onChange={onGroupNameChange}
								disabled={!crud.includes('U')}
							/>

							{errorMessage !== '' && <ErrorText>{errorMessage}</ErrorText>}

							<View className="actionButtonView">
								{crud.includes('D') && (
									<ActionButton
										title="그룹 삭제"
										tooltip="그룹 삭제"
										variant="text"
										onClick={() => onDeleteAlarm()}
										iconName="trash-can-outline"
									/>
								)}

								{crud.includes('U') && (
									<ActionButton
										title="저장"
										tooltip="저장"
										variant="outlined"
										onClick={() => onSave()}
										iconName="check"
									/>
								)}
							</View>
						</View>

						<View className="accessGroupI18nView">
							<I18nEditor
								title="다국어 관리"
								className="accessGroupI18n"
								i18n={selectedAccessGroup.i18n}
								tableName="ACCESS_GROUP"
								tableId={selectedAccessGroup.id}
								menuId=""
								value=""
								disabled={!crud.includes('U') || selectedAccessGroup.id === ''}
								onSave={onI18nEditorSave}
							/>
						</View>
					</View>
				</>
			)}

			<Modal
				target="newGroup"
				title="그룹 삭제"
				show={showModal}
				onSubmit={onDelete}
				onCloseModal={() => setShowModal(false)}
				closeStatus="inherit"
				closeTitle="닫기"
				submitTitle="삭제"
				submitStatus="error"
				submitIcon="trash-can-outline"
			>
				<View css={{ width: '30rem', maxWidth: '100%' }}>
					<Text category="p2">이 그룹을 삭제하면 복구할 수 없습니다.</Text>
					<Text category="p2">그래도 삭제하시겠습니까?</Text>
				</View>
			</Modal>
		</View>
	)
}

const style = css({
	fontFamily: boFont.ui,

	'.editGroupOuter': {
		width: '100%',
		height: '100%',

		'.editGroupView': {
			position: 'relative',
			display: 'flex',
			flexDirection: 'column',
			gap: 12,
			width: '100%',
			padding: '20px 20px 16px',
			borderBottom: `1px solid ${mono.line}`,

			'.editGroupLabel': {
				fontSize: 11,
				letterSpacing: '.18em',
				textTransform: 'uppercase',
				fontWeight: 500,
				color: mono.faint,
			},

			'.actionButtonView': {
				display: 'flex',
				justifyContent: 'flex-end',
				gap: 8,
				paddingTop: 8,
			},
		},

		'.accessGroupI18nView': {
			padding: '16px 20px',
			'.accessGroupI18n': {},
		},
	},
})
